<template>
  <div class="row">
    <div
      class="col-xs-12 col-sm-8 col-xl-6"
      :class="{ 'col-lg-12': canSeeExtraDetails, 'col-lg-6': cannotSeeExtraDetails }">
      <p>
        <strong>Additional Charge</strong><br>
        {{ decorated.description }}
      </p>
    </div>

    <div
      class="col-xs-6 col-sm-4"
      :class="{ 'col-lg-3 col-xl-1': canSeeExtraDetails, 'col-lg-2 col-xl-2': cannotSeeExtraDetails }">
      <p>
        <strong>Charge Type</strong><br>
        {{ decorated.type }}
      </p>
    </div>

    <template v-if="canSeeExtraDetails">
      <div class="col-xs-6 col-sm-4 col-lg-3 col-xl-1">
        <p>
          <strong>Client Rate</strong><br>
          {{ decorated.client_rate }}
        </p>
      </div>
      <div class="col-xs-6 col-sm-4 col-lg-3 col-xl-1">
        <p>
          <strong>
            Rate
            <span
              :class="{ 'text-success': chargeIsProfitable, 'text-danger': chargeIsNotProfitable }"
              :title="perTonneDifference">
              {{ profitAsMoney }}
            </span>
          </strong>
          <br>
          {{ decorated.carrier_rate }}
        </p>
      </div>
    </template>

    <template v-else>
      <div class="col-xs-6 col-sm-4 col-lg-2 col-xl-2">
        <p>
          <strong>Rate</strong><br>
          {{ $user.isCarrierOrDriver ? decorated.carrier_rate : decorated.client_rate }}
        </p>
      </div>
    </template>

    <div
      class="col-xs-6 col-sm-4"
      :class="{ 'col-lg-3 col-xl-1': canSeeExtraDetails, 'col-lg-2 col-xl-2': cannotSeeExtraDetails }">
      <p class="load-cost">
        <strong>Additional Charge</strong><br>

        {{ additionalChargesTotalAsMoney }}
      </p>
    </div>
  </div>
</template>

<script>
  import { ChargeDecorator } from 'decorators'
  import accounting from 'accounting'
  import { loadHelpers } from 'mixins/loads'

  export default {
    props: ['charge', 'load'],

    mixins: [loadHelpers],

    computed: {
      /**
       * Determine if user cann see extra charge details.
       *
       * @return boolean
       */
      canSeeExtraDetails () {
        return this.$user.isAdminOrCoordinator
      },

      /**
       * Determine if user cannot see extra charge details.
       *
       * @return boolean
       */
      cannotSeeExtraDetails () {
        return !this.canSeeExtraDetails
      },

      /**
       * Get the decorated charge.
       *
       * @return object
       */
      decorated () {
        return new ChargeDecorator(this.charge)
      },

      /**
       * Get the load size, will either by the estimated load size or the net weight
       * provided by an approved weighbridge.
       *
       * @return float
       */
      loadSize () {
        if (this.load.weighbridge !== null && this.load.weighbridge.status === 'approved') {
          return this.load.weighbridge.total_tonnage
        } else {
          return parseFloat(this.load.size) || 0
        }
      },

      /**
       * Get the profit arrow will make off of the charge.
       *
       * @return float
       */
      profit () {
        let clientRate = parseFloat(this.charge.client_rate) || 0
        let carrierRate = parseFloat(this.charge.carrier_rate) || 0

        let rateDifference = clientRate - carrierRate

        if (this.charge.type === 'per_tonne') {
          return rateDifference * this.loadSize
        }

        return rateDifference
      },

      /**
       * Get the per tonne difference in client rate and carrier rate.
       *
       * This is the earning (per tonne) that Arrow makes.
       *
       * @return string
       */
      perTonneDifference () {
        if (this.charge.type === 'single') {
          return
        }

        let clientRate = parseFloat(this.charge.client_rate) || 0
        let carrierRate = parseFloat(this.charge.carrier_rate) || 0

        return `${accounting.formatMoney(clientRate - carrierRate)} per tonne`
      },

      /**
       * Determine if charge is profitable.
       *
       * @return boolean
       */
      chargeIsProfitable () {
        return this.profit > 0
      },

      /**
       * Determine if charge is not profitable.
       *
       * @return boolean
       */
      chargeIsNotProfitable () {
        return this.profit <= 0
      },

      /**
       * Get the charges profit in money.
       *
       * @return string
       */
      profitAsMoney () {
        return accounting.formatMoney(this.profit)
      },

      /**
       * Get the client charge total, either estimated based on the load size
       * or actual based on the weighbridge total tonnage.
       *
       * @return string
       */
      clientCharge () {
        let clientRate = parseFloat(this.charge.client_rate) || 0

        if (this.charge.type === 'per_tonne') {
          return accounting.formatMoney(clientRate * this.loadSize)
        }

        return accounting.formatMoney(clientRate)
      }
    }
  }
</script>



// WEBPACK FOOTER //
// Charge.vue?5eecbef1